/**
 * API bases come from build-time env only (`REACT_APP_*`). Do not hardcode production
 * URLs here — set them in `.env.local` (local) or CI/CD (production).
 *
 * See `Website/.env.example` (copy to `.env.local` for dev). Never commit secrets.
 */

function trimBase(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  return s.replace(/\/$/, "");
}

let warnedMissingMainApi;

function warnMissingMainApiOnce() {
  if (warnedMissingMainApi) return;
  warnedMissingMainApi = true;
  if (typeof console === "undefined") return;
  const msg =
    "[config/api] REACT_APP_API_BASE is not set. Copy Website/.env.example to .env.local " +
    "or inject REACT_APP_API_BASE in your CI build (see comments in .env.example).";
  if (process.env.NODE_ENV === "production" && console.error) {
    console.error(msg);
  } else if (console.warn) {
    console.warn(msg);
  }
}

export function getApiBase() {
  const base = trimBase(process.env.REACT_APP_API_BASE);
  if (!base) {
    warnMissingMainApiOnce();
  }
  return base;
}

/** True when main API base is non-empty (safe for fetch in production builds). */
export function isMainApiConfigured() {
  return Boolean(trimBase(process.env.REACT_APP_API_BASE));
}

/** True when OTP Lambda base is non-empty. */
export function isOtpApiConfigured() {
  return Boolean(trimBase(process.env.REACT_APP_OTP_API_BASE));
}

let warnedMissingOtpApi;

function warnMissingOtpApiOnce() {
  if (warnedMissingOtpApi) return;
  warnedMissingOtpApi = true;
  if (typeof console === "undefined") return;
  const msg =
    "[config/api] REACT_APP_OTP_API_BASE is not set. OTP/email calls will fail until " +
    "you set it in .env.local or CI (see Website/.env.example).";
  if (process.env.NODE_ENV === "production" && console.error) {
    console.error(msg);
  } else if (console.warn) {
    console.warn(msg);
  }
}

export function getOtpApiBase() {
  const base = trimBase(process.env.REACT_APP_OTP_API_BASE);
  if (!base) {
    warnMissingOtpApiOnce();
  }
  return base;
}

/**
 * @param {string} route - Lambda route name and optional query, e.g. "getUser?123" or "/getUser"
 */
export function apiUrl(route) {
  const r = route.startsWith("/") ? route.slice(1) : route;
  return `${getApiBase()}/${r}`;
}

/**
 * @param {string} route - OTP/email Lambda route: `getOTP` (POST `{ email }`) or `sendMail` (POST `{ email, subject, text, html }`).
 */
export function otpApiUrl(route) {
  const r = route.startsWith("/") ? route.slice(1) : route;
  return `${getOtpApiBase()}/${r}`;
}

/** POST target for presigned S3 upload; override if the presign Lambda uses a different route. */
export function getPresignUploadUrl() {
  const override = trimBase(process.env.REACT_APP_PRESIGN_UPLOAD_URL);
  if (override) return override;
  return `${getApiBase()}/getPresignedUploadUrl`;
}

/** Authorization only (for GETs and custom bodies). */
export function authFetchHeaders() {
  const h = {};
  try {
    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    // ignore
  }
  return h;
}

/** JSON POST with Bearer JWT when logged in. */
export function jsonAuthHeaders() {
  return { "Content-Type": "application/json", ...authFetchHeaders() };
}
